/* eslint-disable max-lines */
import { useArtworkForm } from "@design-lab/entities/artwork";
import {
  currentPinningSessionAtom,
  designProjectPlacementIdsAtom,
  getPinningSessionForPlacement,
  pendingInitialMessageAtom,
  pinningEnabledArtworksAtom,
  pinningModeAtom,
  pinningSessionsAtom,
  useCreatePinningSession,
  type DesignLabPlacementKey
} from "@design-lab/entities/pinning";
import { cn } from "@design-lab/shared";
import {
  trackDesignLabPinningModeEntered,
  trackDesignLabPinsCtaClicked
} from "@design-lab/shared/lib/analytics";
import {
  useDesignLabPinSessionRecording,
  usePinBasedRedrawEnabled
} from "@design-lab/shared/lib/config";
import { startPinSessionRecording } from "@design-lab/shared/lib/pinSessionRecording";
import type { ArtworkForm } from "@design-lab/shared/types";
import { useToast } from "@design-lab/shared/ui";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { Textarea } from "@design-lab/shared/ui/textarea";
import type { models } from "jiffy-api";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useState, type KeyboardEvent } from "react";
import { useWatch } from "react-hook-form";
import { CompletedSessionIndicator } from "./CompletedSessionIndicator";

type RequestDesignChangesProps = {
  placementSide?: "front" | "back";
  currentValue?: models.cart.madeAdd.EnhancementTier | undefined;
  removeBackground?: boolean;
  canRemoveBackground?: boolean;
  editImageManual?: boolean;
};

export function RequestDesignChanges({
  placementSide = "front",
  currentValue,
  removeBackground = false,
  canRemoveBackground = true,
  editImageManual = false
}: RequestDesignChangesProps) {
  const { form } = useArtworkForm();
  const setPinningMode = useSetAtom(pinningModeAtom);
  const [pinningSessions, setPinningSessions] = useAtom(pinningSessionsAtom);
  const designProjectPlacementIds = useAtomValue(designProjectPlacementIdsAtom);
  const setCurrentPinningSession = useSetAtom(currentPinningSessionAtom);
  const setPendingInitialMessage = useSetAtom(pendingInitialMessageAtom);
  const { mutate: createPinningSession, isPending: isCreatingPinningSession } =
    useCreatePinningSession();
  const { error: showErrorToast } = useToast();
  const pinBasedRedrawEnabled = usePinBasedRedrawEnabled();
  const pinSessionRecordingEnabled = useDesignLabPinSessionRecording();
  const pinningEnabledArtworks = useAtomValue(pinningEnabledArtworksAtom);
  const [inputMessage, setInputMessage] = useState("");

  const artworkForm = useWatch({
    control: form.control
  }) as ArtworkForm | undefined;

  const selectedFrontPlacement = artworkForm?.selectedFrontPlacement as
    | DesignLabPlacementKey
    | undefined;
  let placementKey: DesignLabPlacementKey = "front";
  if (placementSide === "back") {
    placementKey = "back";
  } else if (selectedFrontPlacement === "frontLeftChest") {
    placementKey = "frontLeftChest";
  }

  const artworkIdForm = (
    artworkForm?.[placementKey]?.artworkId ? String(artworkForm[placementKey]?.artworkId) : ""
  ) as string;

  // Check if pinning is enabled for this artwork (based on ownership)
  // If artworkId is not in the map, assume enabled (backwards compatibility)
  const isPinningEnabled = artworkIdForm ? (pinningEnabledArtworks[artworkIdForm] ?? true) : true;

  const { designProjectPlacementId, session: sessionForPlacement } = getPinningSessionForPlacement({
    placementKey,
    designProjectPlacementIds,
    pinningSessions
  });
  const hasActiveSession = pinBasedRedrawEnabled && sessionForPlacement?.state === "active";
  const hasCompletedSession = sessionForPlacement?.state === "completed";

  // Determine the preview image name based on current selection
  // This matches the backend logic in dtf_print.rb artwork_preview_image_name
  const getPreviewImageName = useCallback((): string | undefined => {
    if (!currentValue) return undefined;

    if (currentValue === "print_original") {
      return "original_image";
    }

    if (currentValue === "full_enhancements") {
      if (editImageManual) {
        return "manual_edit_image";
      }
      if (removeBackground && canRemoveBackground) {
        return "image_no_background";
      }
      return "image_with_background";
    }

    return undefined;
  }, [currentValue, removeBackground, canRemoveBackground, editImageManual]);

  const handleRequestDesignerEdit = useCallback(
    (message: string) => {
      if (isCreatingPinningSession) return;
      const artworkId = artworkIdForm;
      if (!artworkId) return;
      if (!designProjectPlacementId) {
        showErrorToast({
          title: "Unable to start design instructions",
          description: "Please wait a moment and try again."
        });
        return;
      }

      const previewImageName = getPreviewImageName();

      trackDesignLabPinsCtaClicked(artworkId);

      // Reuse existing active session if one exists for this artwork
      const existingSession = sessionForPlacement;
      if (existingSession?.state === "active") {
        trackDesignLabPinningModeEntered({
          artworkId,
          sessionId: existingSession.sessionId,
          hasExistingPins: true,
          initialMessage: message
        });
        setPendingInitialMessage(message);
        startPinSessionRecording({ enabled: pinSessionRecordingEnabled });
        setCurrentPinningSession({
          designProjectPlacementId,
          artworkId,
          sessionId: existingSession.sessionId
        });
        setPinningMode(true);
        return;
      }

      const sessionParams = {
        designProjectPlacementId,
        artworkId,
        ...(previewImageName && { previewImageName })
      };

      createPinningSession(sessionParams, {
        onSuccess: (data: { sessionId: string }) => {
          trackDesignLabPinningModeEntered({
            artworkId,
            sessionId: data.sessionId,
            hasExistingPins: false,
            initialMessage: message
          });
          setPendingInitialMessage(message);
          startPinSessionRecording({ enabled: pinSessionRecordingEnabled });
          setPinningSessions((prev: typeof pinningSessions) => ({
            ...prev,
            [designProjectPlacementId]: {
              sessionId: data.sessionId,
              state: "active",
              summary: null
            }
          }));
          setCurrentPinningSession({
            designProjectPlacementId,
            artworkId,
            sessionId: data.sessionId
          });
          setPinningMode(true);
        },
        onError: () => {
          showErrorToast({
            title: "Unable to start design instructions",
            description: "Something went wrong. Please try again."
          });
        }
      });
    },
    [
      isCreatingPinningSession,
      artworkIdForm,
      designProjectPlacementId,
      getPreviewImageName,
      sessionForPlacement,
      createPinningSession,
      setPinningSessions,
      setCurrentPinningSession,
      setPinningMode,
      setPendingInitialMessage,
      pinSessionRecordingEnabled,
      showErrorToast
    ]
  );

  const handleSubmitMessage = useCallback(() => {
    const trimmed = inputMessage.trim();
    if (!trimmed) return;
    handleRequestDesignerEdit(trimmed);
  }, [inputMessage, handleRequestDesignerEdit]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        handleSubmitMessage();
      }
    },
    [handleSubmitMessage]
  );

  // Completed pinning session indicator
  if (hasCompletedSession) {
    return <CompletedSessionIndicator placementKey={placementKey} placementSide={placementSide} />;
  }

  if (!pinBasedRedrawEnabled || hasActiveSession || !isPinningEnabled) {
    return null;
  }

  return (
    <div className="mb-4 overflow-hidden rounded-md border border-blue-200 focus-within:border-blue-600">
      <div className="bg-blue-50 px-3 py-2">
        <span className="text-sm font-semibold text-neutral-950">Get Free Artist Edits</span>
      </div>
      <div className="flex flex-col gap-2 px-3 py-2">
        <Textarea
          data-testid="request-designer-edit-input"
          className="min-h-0 resize-none border-0 p-0 shadow-none focus-visible:ring-0"
          placeholder="Type your edit and we'll guide you through it"
          rows={2}
          value={inputMessage}
          onChange={e => setInputMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isCreatingPinningSession}
        />
        <div className="flex justify-end">
          <Button
            data-testid="request-designer-edit-submit"
            size="sm"
            className={cn(
              "bg-blue-600 hover:bg-blue-700",
              !inputMessage.trim() && !isCreatingPinningSession && "invisible"
            )}
            onPress={handleSubmitMessage}
            isDisabled={isCreatingPinningSession}
          >
            {isCreatingPinningSession ? "Submitting\u2026" : "Submit"}
          </Button>
        </div>
      </div>
    </div>
  );
}
